import React, { useState } from 'react';

const WordCloud = ({ selectedRegion, selectedCity, dateRange, showLocations, showProducts, showApps }) => {
  const [filter, setFilter] = useState('all'); // 'all', 'positive', 'negative'
  
  // Mock data for word cloud
  // In a real implementation, this would be generated from review text analysis
  const words = [
    { text: 'Quality', size: 80, sentiment: 'positive' },
    { text: 'Service', size: 75, sentiment: 'mixed' },
    { text: 'Delivery', size: 70, sentiment: 'negative' },
    { text: 'Price', size: 65, sentiment: 'mixed' },
    { text: 'Support', size: 60, sentiment: 'negative' },
    { text: 'Speed', size: 55, sentiment: 'positive' },
    { text: 'Easy', size: 52, sentiment: 'positive' },
    { text: 'Reliable', size: 50, sentiment: 'positive' },
    { text: 'Interface', size: 48, sentiment: 'negative' },
    { text: 'Value', size: 46, sentiment: 'positive' },
    { text: 'Features', size: 44, sentiment: 'positive' },
    { text: 'Bugs', size: 42, sentiment: 'negative' },
    { text: 'Warranty', size: 40, sentiment: 'mixed' },
    { text: 'Shipping', size: 38, sentiment: 'negative' },
    { text: 'Durability', size: 36, sentiment: 'positive' },
    { text: 'Packaging', size: 34, sentiment: 'mixed' },
    { text: 'Performance', size: 32, sentiment: 'positive' },
    { text: 'Design', size: 30, sentiment: 'positive' },
    { text: 'Cost', size: 28, sentiment: 'negative' },
    { text: 'Efficiency', size: 26, sentiment: 'positive' },
    { text: 'Usability', size: 24, sentiment: 'mixed' },
    { text: 'Response', size: 22, sentiment: 'negative' },
    { text: 'Functionality', size: 20, sentiment: 'positive' },
  ];
  
  // Filter words based on selected sentiment filter
  const filteredWords = words.filter(word => {
    if (filter === 'all') return true;
    if (filter === 'positive') return word.sentiment === 'positive';
    if (filter === 'negative') return word.sentiment === 'negative';
    return true;
  }).sort((a, b) => b.size - a.size); // Sort by size to place largest words first
  
  // Function to get text color based on sentiment
  const getWordColor = (sentiment) => {
    switch (sentiment) {
      case 'positive':
        return 'text-green-600';
      case 'negative':
        return 'text-red-600';
      case 'mixed':
      default:
        return 'text-blue-600';
    }
  };
  
  // Function to get font size based on word size (frequency)
  const getFontSize = (size) => {
    // Map the size to a font size between 12px and 24px
    return Math.max(12, Math.min(24, 12 + (size / 80) * 12));
  };

  // Generate a vertical offset for each word to create a more natural look
  const getRandomVerticalOffset = (index) => {
    // Create a deterministic but varied pattern based on index
    // This ensures words don't all line up in a straight line
    const offsets = [-4, -2, 0, 2, 4];
    return offsets[index % offsets.length];
  };
  
  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-800">Word Cloud</h2>
        
        <div className="flex bg-gray-50 p-1 rounded-md">
          <button
            className={`px-3 py-1 text-xs rounded-md ${
              filter === 'all'
                ? 'bg-blue-100 text-blue-700 font-medium'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            onClick={() => setFilter('all')}
          >
            All Words
          </button>
          <button
            className={`px-3 py-1 text-xs rounded-md ${
              filter === 'positive'
                ? 'bg-green-100 text-green-700 font-medium'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            onClick={() => setFilter('positive')}
          >
            Positive
          </button>
          <button
            className={`px-3 py-1 text-xs rounded-md ${
              filter === 'negative'
                ? 'bg-red-100 text-red-700 font-medium'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            onClick={() => setFilter('negative')}
          >
            Negative
          </button>
        </div>
      </div>
      
      <div className="bg-gray-50 p-2 rounded-lg h-80 overflow-y-auto flex items-center">
        <div className="flex flex-wrap justify-center gap-x-1 w-full">
          {filteredWords.map((word, index) => (
            <div
              key={index}
              className={`cursor-pointer font-medium inline-block text-center leading-tight ${getWordColor(word.sentiment)}`}
              style={{
                fontSize: `${getFontSize(word.size)}px`,
                transform: `translateY(${getRandomVerticalOffset(index)}px)`,
                margin: '0 2px',
                padding: '0',
              }}
              title={`${word.text}: mentioned in ${word.size}% of reviews`}
            >
              {word.text}
            </div>
          ))}
        </div>
      </div>
      
      <div className="mt-4 flex justify-center">
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-green-600 mr-1"></div>
            <span className="text-xs text-gray-600">Positive</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-blue-600 mr-1"></div>
            <span className="text-xs text-gray-600">Mixed</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-red-600 mr-1"></div>
            <span className="text-xs text-gray-600">Negative</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WordCloud;